import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Chip, List, ListItemButton, CircularProgress,
  InputAdornment, Tooltip, IconButton, ToggleButton, ToggleButtonGroup, Avatar,
  Divider, Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  WhatsApp as WhatsAppIcon,
  Forum as ForumIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  ChatBubbleOutline as CommentIcon,
  MailOutline as DmIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ---------------------------------------------------------------------------
// Bandeja unificada de Redes Sociales (Fase 3).
// Reúne conversaciones de WhatsApp / Instagram / Facebook en una sola vista.
// Las de IG/FB solo aparecen cuando el connector está activo (flag ENABLE_IG_FB_BOT)
// y Meta aprueba el App Review; mientras tanto la vista social sale vacía (estado honesto).
// Fuente de datos: apiService.getBotConversations() (mismo proxy del CRM de WhatsApp),
// con filtrado por `channel` en el cliente (el proxy no filtra por canal todavía).
// ---------------------------------------------------------------------------

type Channel = 'instagram' | 'facebook' | 'whatsapp';
type ChannelFilter = 'social' | 'instagram' | 'facebook' | 'whatsapp' | 'all';

interface Convo {
  phoneNumber: string; // clave de la conversación (para IG/FB es ig_<id> / fb_<id>)
  contactName?: string;
  leadName?: string;
  lastBody?: string;
  lastSender?: string;
  lastMessageAt?: string;
  channel?: string;    // 'instagram' | 'facebook' | ausente => whatsapp
  eventType?: string;  // 'comment' | 'dm' (solo IG/FB)
  status?: string;
}

interface Msg {
  body: string;
  sender: 'client' | 'bot' | 'assistant' | 'operator';
  timestamp: string | number;
}

interface ConvoDetail extends Convo {
  messages: Msg[];
}

function chanOf(c: { channel?: string }): Channel {
  const v = (c.channel || '').toLowerCase();
  if (v === 'instagram') return 'instagram';
  if (v === 'facebook') return 'facebook';
  return 'whatsapp';
}

function formatTs(ts?: string | number): string {
  if (!ts) return '';
  const n = typeof ts === 'number' ? (ts < 1e12 ? ts * 1000 : ts) : ts;
  const d = new Date(n);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
}

const ChannelBadge: React.FC<{ channel: Channel; size?: number }> = ({ channel, size = 22 }) => {
  const map = {
    instagram: { color: '#C13584', icon: <InstagramIcon sx={{ fontSize: size * 0.62 }} /> },
    facebook: { color: '#1877F2', icon: <FacebookIcon sx={{ fontSize: size * 0.62 }} /> },
    whatsapp: { color: '#25D366', icon: <WhatsAppIcon sx={{ fontSize: size * 0.62 }} /> },
  }[channel];
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: map.color }}>{map.icon}</Avatar>
  );
};

const SocialInboxPage: React.FC = () => {
  const [convos, setConvos] = useState<Convo[]>([]);
  const [selected, setSelected] = useState<ConvoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filter, setFilter] = useState<ChannelFilter>('social');
  const [search, setSearch] = useState('');
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const fetchConvos = useCallback(async () => {
    try {
      const result = await apiService.getBotConversations();
      setConvos(result?.data || []);
    } catch (e: any) {
      console.error('[SocialInbox] fetch:', e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConvos();
    const id = setInterval(fetchConvos, 30000);
    return () => clearInterval(id);
  }, [fetchConvos]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.messages?.length]);

  const handleSelect = useCallback(async (c: Convo) => {
    setLoadingDetail(true);
    setSelected({ ...c, messages: [] });
    try {
      const result = await apiService.getBotConversation(c.phoneNumber);
      setSelected({ ...c, ...(result?.data || {}), messages: result?.data?.messages || [] });
    } catch (e: any) {
      console.error('[SocialInbox] detail:', e?.message);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return convos.filter((c) => {
      const ch = chanOf(c);
      const passChannel =
        filter === 'all' ? true :
        filter === 'social' ? (ch === 'instagram' || ch === 'facebook') :
        ch === filter;
      if (!passChannel) return false;
      if (!q) return true;
      const hay = `${c.contactName || ''} ${c.leadName || ''} ${c.lastBody || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [convos, filter, search]);

  const counts = useMemo(() => {
    const c = { instagram: 0, facebook: 0, whatsapp: 0 };
    for (const v of convos) c[chanOf(v)]++;
    return c;
  }, [convos]);

  const selectedChannel = selected ? chanOf(selected) : 'whatsapp';
  const replyDisabled = true; // routing manual IG/FB aún no construido (ver flag abajo)

  return (
    <Box sx={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header + filtros */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <ForumIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Bandeja unificada</Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small" exclusive value={filter}
          onChange={(_, v) => v && setFilter(v)}
        >
          <ToggleButton value="social">Social</ToggleButton>
          <ToggleButton value="instagram"><InstagramIcon sx={{ fontSize: 18, mr: 0.5 }} />IG ({counts.instagram})</ToggleButton>
          <ToggleButton value="facebook"><FacebookIcon sx={{ fontSize: 18, mr: 0.5 }} />FB ({counts.facebook})</ToggleButton>
          <ToggleButton value="whatsapp"><WhatsAppIcon sx={{ fontSize: 18, mr: 0.5 }} />WA ({counts.whatsapp})</ToggleButton>
          <ToggleButton value="all">Todo</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refrescar">
          <IconButton size="small" onClick={fetchConvos}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        {/* Lista */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1 }}>
              <TextField
                size="small" fullWidth placeholder="Buscar..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  {(filter === 'social' || filter === 'instagram' || filter === 'facebook') ? (
                    <>
                      <ForumIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body2">
                        Aún no hay conversaciones de IG/FB.
                      </Typography>
                      <Typography variant="caption">
                        Se activan al aprobar el App Review de Meta y encender el bot social.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2">No hay conversaciones.</Typography>
                  )}
                </Box>
              ) : (
                <List disablePadding>
                  {filtered.map((c) => {
                    const ch = chanOf(c);
                    const name = c.leadName || c.contactName || c.phoneNumber;
                    const isSel = selected?.phoneNumber === c.phoneNumber;
                    return (
                      <ListItemButton
                        key={c.phoneNumber} selected={isSel} onClick={() => handleSelect(c)}
                        sx={{ alignItems: 'flex-start', gap: 1, py: 1 }}
                      >
                        <ChannelBadge channel={ch} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {name}
                            </Typography>
                            {c.eventType && (
                              <Chip
                                size="small"
                                icon={c.eventType === 'comment' ? <CommentIcon sx={{ fontSize: 13 }} /> : <DmIcon sx={{ fontSize: 13 }} />}
                                label={c.eventType === 'comment' ? 'Comentario' : 'DM'}
                                sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: '0.6rem' } }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.lastBody || ''}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          {formatTs(c.lastMessageAt)}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Hilo */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {!selected ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                <ForumIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
                <Typography variant="body2">Selecciona una conversación</Typography>
              </Box>
            ) : (
              <>
                {/* Cabecera del hilo */}
                <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ChannelBadge channel={selectedChannel} size={30} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {selected.leadName || selected.contactName || selected.phoneNumber}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                      {selectedChannel}{selected.eventType ? ` · ${selected.eventType === 'comment' ? 'Comentario' : 'DM'}` : ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Mensajes */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {loadingDetail ? (
                    <CircularProgress sx={{ m: 'auto' }} />
                  ) : (selected.messages || []).length === 0 ? (
                    <Typography variant="body2" sx={{ m: 'auto', color: 'text.secondary' }}>Sin mensajes.</Typography>
                  ) : (
                    (selected.messages || []).map((msg, i) => {
                      const isClient = msg.sender === 'client';
                      const isBot = msg.sender === 'bot';
                      const isOperator = msg.sender === 'operator';
                      const bgColor = isClient ? '#fff' : isBot ? '#e3f2fd' : isOperator ? '#fff3e0' : '#e8f5e9';
                      const align = isClient ? 'flex-start' : 'flex-end';
                      const senderLabel = isBot ? 'Sofia' : isOperator ? 'Operador' : msg.sender === 'assistant' ? 'Portal' : '';
                      return (
                        <Box key={i} sx={{ display: 'flex', justifyContent: align }}>
                          <Box sx={{ maxWidth: '75%', bgcolor: bgColor, borderRadius: 2, px: 1.5, py: 0.8, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                            {senderLabel && <Typography variant="caption" sx={{ fontWeight: 600, color: isBot ? '#1565c0' : isOperator ? '#e65100' : '#2e7d32', display: 'block', fontSize: '0.65rem' }}>{senderLabel}</Typography>}
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>{msg.body}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.3, fontSize: '0.65rem' }}>
                              {formatTs(msg.timestamp)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </Box>

                {/* Barra de respuesta (deshabilitada por ahora para IG/FB) */}
                {replyDisabled && (
                  <Alert severity="info" sx={{ mx: 1, mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    El bot responde automático en IG/FB. La respuesta manual desde el portal está en camino.
                  </Alert>
                )}
                <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0, bgcolor: 'background.paper' }}>
                  <Tooltip title="Respuesta manual a IG/FB: próximamente (el bot responde automático)">
                    <TextField
                      size="small" fullWidth multiline maxRows={4}
                      placeholder="Respuesta manual próximamente..."
                      disabled
                      inputProps={{ style: { fontSize: 16 } }}
                    />
                  </Tooltip>
                  <IconButton color="primary" disabled><SendIcon /></IconButton>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SocialInboxPage;
